/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PALETTE } from "styles/base/variables";
import {
  SeveranceRequestPanel,
  SeveranceRequestView
} from "models/payrollManagement.model";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // §2.10 패널 (부모가 조회해서 내려준다). null = 로딩 전/실패
  panel: SeveranceRequestPanel | null;
  yearMonth: string; // YYYY-MM (화면 지급월)
  // 정산 완료/해제 후 부모가 패널·그리드를 다시 조회하도록
  onChanged: () => void;
}

type Tab = "wage" | "sev";

// 매트릭스 테이블의 상태 dot과 동일 (PayrollGrid의 C.good / C.warn)
const DOT_DONE = PALETTE.progressCompleted; // 완료 — 채운 초록
const DOT_TODO = "#F5A623"; // 조치 필요 — 주황 테두리

const pensionLabel = (p: SeveranceRequestView["pensionType"]) =>
  p === "DC" ? "DC형" : p === "DB" ? "DB형" : p === "NONE" ? "미가입" : "-";

const payMonthLabel = (m: string | null) =>
  m ? `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월` : "-";

// 퇴사사유 = Payee.resignReasonCode/Text. 상실신고·재직상태 변경 등 경로에 따라 설명(text) 없이
// 코드만 저장되기도 하므로 코드로 문구를 채운다 (고용보험 상실코드 — 세특 LOSS_REASONS와 동일)
export const LEAVE_REASON_LABEL: Record<string, string> = {
  "11": "개인사정으로 인한 자진퇴사",
  "12": "사업장 이전·근로조건 변동·임금체불 등으로 자진퇴사",
  "22": "폐업·도산",
  "23": "경영상 필요 및 회사불황으로 인원감축 등에 의한 퇴사",
  "26": "근로자의 귀책사유에 의한 징계해고·권고사직",
  "31": "정년",
  "32": "계약기간 만료·공사종료",
  "41": "고용보험 비적용",
  "42": "이중고용"
};

const leaveReasonLabel = (v: SeveranceRequestView) => {
  const code = v.leaveReasonCode?.trim();
  const text = v.leaveReasonText?.trim() || (code && LEAVE_REASON_LABEL[code]);
  if (code && text) return `${code} ${text}`;
  return text || code || null;
};

// 법정지급기한 = 퇴사일 + 15일 (YYYY-MM-DD)
const legalDueDate = (leaveDate: string) => {
  const [y, m, d] = leaveDate.split("-").map(Number);
  const dt = new Date(y, m - 1, d + 15);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(dt.getDate()).padStart(2, "0")}`;
};

function SeveranceRequestDialog({ isOpen, onClose, panel }: Props) {
  const [tab, setTab] = useState<Tab>("wage");

  useEffect(() => {
    if (isOpen) setTab("wage");
  }, [isOpen]);

  const wageList = panel?.incomeTaxSettlements ?? [];
  const sevList = panel?.severancePaySettlements ?? [];
  // 탭2 건수: 이미 정산 완료된 건은 제외한다.
  const sevPendingCount = sevList.filter((v) => !v.settledAt).length;

  const thCss = css`
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: ${PALETTE.textMid};
    padding: 0 10px 6px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    white-space: nowrap;
  `;
  const tdCss = css`
    padding: 9px 10px 9px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    vertical-align: top;
    font-size: 13px;
    line-height: 1.5;
  `;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        css={css`
          font-size: 16px;
          font-weight: 700;
        `}
      >
        퇴사자 정산요청
        <span
          css={css`
            font-weight: 400;
            font-size: 12px;
            color: ${PALETTE.textMid};
            margin-left: 8px;
          `}
        >
          거래처가 세특 [급여입력]에서 보낸 요청 내역입니다 — 조회 전용
        </span>
        <IconButton
          onClick={onClose}
          css={css`
            position: absolute;
            right: 8px;
            top: 8px;
          `}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            display: flex;
            gap: 6px;
            border-bottom: 1px solid ${PALETTE.borderLight};
            margin-bottom: 12px;
          `}
        >
          {(
            [
              ["wage", `퇴사자 소득세 정산 ${wageList.length}`],
              ["sev", `퇴직금 정산 ${sevPendingCount}`]
            ] as [Tab, string][]
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              css={css`
                border: 0;
                background: transparent;
                font: inherit;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                padding: 7px 12px 9px;
                color: ${tab === t ? PALETTE.text : PALETTE.textMid};
                border-bottom: 2px solid
                  ${tab === t ? PALETTE.primary : "transparent"};
              `}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "wage" ? (
          <>
            <p
              css={css`
                margin: 0 0 8px;
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              이 화면의 지급월에 담기는 퇴사자입니다. 담당자가 세특 [급여입력]에
              소득세 정산을 입력하고 [입력완료]하면 급여자료 <b>근로</b> 칸이
              완료됩니다.
            </p>
            <table
              css={css`
                width: 100%;
                border-collapse: collapse;
              `}
            >
              <thead>
                <tr>
                  <th css={thCss}>거래처</th>
                  <th css={thCss}>퇴사자</th>
                  <th css={thCss}>퇴사일</th>
                  <th css={thCss}>법정지급기한</th>
                  <th css={thCss}>퇴사사유</th>
                  <th css={thCss}>진행</th>
                </tr>
              </thead>
              <tbody>
                {wageList.length ? (
                  wageList.map((v) => (
                    <tr key={v.businessId + v.payeeId}>
                      <td css={tdCss}>
                        <b>{v.businessName}</b>
                      </td>
                      <td css={tdCss}>{v.payeeName}</td>
                      <td css={tdCss}>{v.leaveDate}</td>
                      <td css={tdCss}>{legalDueDate(v.leaveDate)}</td>
                      <td css={tdCss}>
                        {leaveReasonLabel(v) ||
                          (v.requested ? "-" : "정산요청 미입력")}
                      </td>
                      <td css={tdCss}>
                        <span
                          css={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 7px;
                          `}
                        >
                          <span
                            css={css`
                              display: inline-block;
                              width: 11px;
                              height: 11px;
                              border-radius: 3px;
                              flex-shrink: 0;
                              ${v.wageProgress === "CLOSED"
                                ? `background: ${DOT_DONE};`
                                : `box-shadow: inset 0 0 0 2px ${DOT_TODO};`}
                            `}
                          />
                          {v.wageProgress === "CLOSED"
                            ? "세특 마감완료"
                            : "세특 정산 대기"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      css={tdCss}
                      colSpan={6}
                      style={{ color: PALETTE.textMid }}
                    >
                      이번 달 지급분에 퇴사자가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        ) : (
          <>
            <p
              css={css`
                margin: 0 0 8px;
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              계속근로 1년 이상 · 퇴직금 지급 건. <b>지급예정월</b>에 처리하며,
              담당자가 세무사랑에서 계산한 뒤 매트릭스 표의 <b>퇴직</b> 칸을
              눌러 완료 처리합니다(이 화면은 조회 전용). DC형은 원천세
              신고·지급명세서 대상이 아닙니다(연금사업자 처리).
            </p>
            <table
              css={css`
                width: 100%;
                border-collapse: collapse;
              `}
            >
              <thead>
                <tr>
                  <th css={thCss}>거래처</th>
                  <th css={thCss}>퇴사자</th>
                  <th css={thCss}>퇴사일</th>
                  <th css={thCss}>퇴직연금</th>
                  <th css={thCss}>지급예정월</th>
                  <th css={thCss}>IRP 계좌</th>
                  <th css={thCss}>원천세</th>
                  <th css={thCss}>퇴직정산</th>
                </tr>
              </thead>
              <tbody>
                {sevList.length ? (
                  sevList.map((v) => {
                    const dc = v.pensionType === "DC";
                    const key = v.businessId + v.payeeId;
                    return (
                      <tr
                        key={key}
                        css={
                          v.payMonthIsCurrent
                            ? css`
                                background: ${PALETTE.primary}0d;
                              `
                            : undefined
                        }
                      >
                        <td css={tdCss}>
                          <b>{v.businessName}</b>
                        </td>
                        <td css={tdCss}>{v.payeeName}</td>
                        <td css={tdCss}>{v.leaveDate}</td>
                        <td css={tdCss}>{pensionLabel(v.pensionType)}</td>
                        <td css={tdCss}>
                          {dc ? "-" : payMonthLabel(v.payMonth)}
                          {v.payMonthIsCurrent && (
                            <span
                              css={css`
                                margin-left: 6px;
                                font-size: 10px;
                                font-weight: 700;
                                color: ${PALETTE.primary};
                                border: 1px solid ${PALETTE.primary};
                                border-radius: 4px;
                                padding: 1px 4px;
                              `}
                            >
                              이번 달
                            </span>
                          )}
                        </td>
                        <td css={tdCss}>
                          {v.toIrp && v.irpAccount
                            ? `${v.irpBank || ""} ${v.irpAccount} (${
                                v.irpHolder || ""
                              })`
                            : "-"}
                        </td>
                        <td css={tdCss}>
                          {v.withholdingTarget ? "대상" : "비대상"}
                        </td>
                        <td css={tdCss}>
                          {dc ? (
                            <span style={{ color: PALETTE.textMid }}>
                              대상 아님
                            </span>
                          ) : (
                            <span
                              css={css`
                                display: inline-flex;
                                align-items: center;
                                gap: 7px;
                              `}
                            >
                              <span
                                css={css`
                                  display: inline-block;
                                  width: 11px;
                                  height: 11px;
                                  border-radius: 3px;
                                  flex-shrink: 0;
                                  ${v.settledAt
                                    ? `background: ${DOT_DONE};`
                                    : `box-shadow: inset 0 0 0 2px ${DOT_TODO};`}
                                `}
                              />
                              {v.settledAt ? "정산 완료" : "정산 대기"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      css={tdCss}
                      colSpan={8}
                      style={{ color: PALETTE.textMid }}
                    >
                      퇴직금 지급 대상이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SeveranceRequestDialog;
